import type { GResponse } from "../../models/common";
import { fetchFromCredentials } from "./credentials-fetch";
import type { Credential, RemoteCredential, UtilityVendor } from "./model";
import { remoteCredentialToLocal } from "./model";

export type CreateCredentialPayloadPortal = Omit<
    import("./model").CreateCredentialPayload,
    "customer_id" | "customer_name"
>;

export type UpdateCredentialPayloadPortal = Omit<
    import("./model").UpdateCredentialPayload,
    "customer_id" | "customer_name"
> & { id: string };

/** org is inferred from token in choripan, never from client params */
export async function getCredentials(): Promise<Credential[]> {
    const url = `/api/v1/credentials?include=Vendor`;

    const data = await fetchFromCredentials<GResponse<RemoteCredential>>(url, {
        headers: {
            "X-Decrypt": "true",
        },
    });

    return data.items.map(remoteCredentialToLocal);
}

export async function createCredential(
    payload: CreateCredentialPayloadPortal,
): Promise<Credential> {
    const url = `/api/v1/credentials`;

    const response = await fetchFromCredentials<RemoteCredential[]>(url, {
        method: "POST",
        headers: {
            "X-Decrypt": "true",
        },
        body: JSON.stringify({ data: [payload] }),
    });

    if (Array.isArray(response) && response.length > 0) {
        return remoteCredentialToLocal(response[0]);
    }

    console.error("Unexpected response format:", response);
    throw new Error(`Invalid response format from credentials API: ${JSON.stringify(response)}`);
}

export async function updateCredential(
    payload: UpdateCredentialPayloadPortal,
): Promise<Credential> {
    const url = `/api/v1/credentials`;

    const cleanPayload = { ...payload };
    if (!cleanPayload.password || cleanPayload.password === "") {
        delete cleanPayload.password;
    }

    const response = await fetchFromCredentials<RemoteCredential[]>(url, {
        method: "POST",
        headers: {
            "X-Decrypt": "true",
        },
        body: JSON.stringify({ data: [cleanPayload] }),
    });

    if (Array.isArray(response) && response.length > 0) {
        return remoteCredentialToLocal(response[0]);
    }

    console.error("Unexpected response format:", response);
    throw new Error(`Invalid response format from credentials API: ${JSON.stringify(response)}`);
}

export async function deleteCredential(
    credentialId: string,
    changedBy?: string | null,
): Promise<void> {
    const url = `/api/v1/credentials/${credentialId}`;

    await fetchFromCredentials<void>(url, {
        method: "DELETE",
        body: JSON.stringify({ changed_by: changedBy || null }),
    });
}

export async function getUtilityVendors(): Promise<GResponse<UtilityVendor>> {
    const url = `/api/v1/vendors?page[limit]=10000`;
    return await fetchFromCredentials<GResponse<UtilityVendor>>(url);
}
